import { KeycloakService } from './keycloakService';

interface TokenExpirationServiceOptions {
  checkInterval?: number;
  onTokenExpired?: () => void;
}

class TokenExpirationService {
  private intervalId: NodeJS.Timeout | null = null;
  private checkInterval: number;
  private onTokenExpired?: () => void;

  constructor(options: TokenExpirationServiceOptions = {}) {
    this.checkInterval = options.checkInterval || 30 * 1000; // 30 seconds default
    this.onTokenExpired = options.onTokenExpired;
  }

  /**
   * Rozpoczyna okresowe sprawdzanie wygaśnięcia tokenu
   */
  start(): NodeJS.Timeout {
    if (this.intervalId) {
      this.stop();
    }

    this.intervalId = setInterval(() => {
      this.checkTokenExpiration();
    }, this.checkInterval);

    return this.intervalId;
  }

  /**
   * Zatrzymuje okresowe sprawdzanie wygaśnięcia tokenu
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Sprawdza czy token wygasł i podejmuje odpowiednie działania
   */
  private checkTokenExpiration(): void {
    if (!KeycloakService.isLoggedIn()) {
      return;
    }

    // Sprawdzamy czy token jest nadal ważny z marginesem bezpieczeństwa
    if (!KeycloakService.isTokenValid(60)) { // 60 sekund marginesu
      console.warn('Token wygasł lub wygaśnie wkrótce, próba odświeżenia...');
      
      KeycloakService.updateToken(60)
        .then((refreshed) => {
          if (refreshed) {
            console.log('Token został odświeżony przez TokenExpirationService');
          } else {
            console.log('Token nie wymagał odświeżenia');
          }
        })
        .catch((error) => {
          console.error('Nie udało się odświeżyć tokenu:', error);
          this.handleTokenExpired();
        });
    }
  }

  /**
   * Obsługuje sytuację gdy token wygasł i nie można go odświeżyć
   */
  private handleTokenExpired(): void {
    console.warn('Token wygasł i nie można go odświeżyć - wylogowywanie użytkownika');
    
    if (KeycloakService.isLoggedIn()) {
      KeycloakService.logout();
    }
    
    this.onTokenExpired?.();
    this.stop(); // Zatrzymujemy sprawdzanie po wylogowaniu
  }

  /**
   * Sprawdza czy serwis jest aktywny
   */
  isActive(): boolean {
    return this.intervalId !== null;
  }

  /**
   * Aktualizuje opcje serwisu
   */
  updateOptions(options: TokenExpirationServiceOptions): void {
    const wasActive = this.isActive();
    
    if (wasActive) {
      this.stop();
    }

    this.checkInterval = options.checkInterval || this.checkInterval;
    this.onTokenExpired = options.onTokenExpired || this.onTokenExpired;

    if (wasActive) {
      this.start();
    }
  }
}

// Eksportujemy singleton instance
export const tokenExpirationService = new TokenExpirationService();

// Eksportujemy również klasę dla przypadków gdy potrzebne są multiple instances
export { TokenExpirationService };